import React, { useEffect, useState } from 'react';
import { TabContent, TabPane, Spinner } from 'reactstrap';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { fetchItemDetails } from '../../../../services/Http/Api';
import PageContainer from '../../../../shared/layout/containers/PageContainer';
import PageHeader from '../../../../shared/layout/headers/PageHeader';
import PageTitle from '../../../../shared/layout/titles/PageTitle';
import PageContent from '../../../../shared/layout/containers/PageContent';
import TabBar from '../../../../shared/layout/tabs/TabBar';
import TabButton from '../../../../shared/layout/tabs/TabButton';
import StyledCard from '../../../../shared/layout/cards/StyledCard';
import { MdDescription, MdHistory, MdInfo, MdSecurity } from 'react-icons/md';
import styled from 'styled-components';
import ItemTransactionsTable from "./ItemTransactionsTable";
import { useCurrency } from '../../../../context/CurrencyProvider';

const ViewItem = () => {
    const { itemId } = useParams();
    const { t } = useTranslation();
    const { formatResolvedAmount } = useCurrency();
    const [activeTab, setActiveTab] = useState('1');
    const [itemDetails, setItemDetails] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const getItemDetails = async () => {
            setLoading(true);
            try {
                const data = await fetchItemDetails(itemId);
                setItemDetails(data);
            } catch (error) {
                console.error('Error fetching item details:', error);
            } finally {
                setLoading(false);
            }
        };

        getItemDetails();
    }, [itemId]);

    const toggleTab = (tab) => {
        if (activeTab !== tab) setActiveTab(tab);
    };

    const isInStock = itemDetails?.current_holder_id && itemDetails?.seller_id
        ? itemDetails.current_holder_id === itemDetails.seller_id
        : null;

    return (
        <PageContainer>
            <PageHeader>
                <PageTitle>
                    {itemDetails?.manufacturer_item?.product_name || t('Items.ViewItem.loading')}
                </PageTitle>
            </PageHeader>
            <PageContent>
                <TabBar>
                    <TabButton
                        active={activeTab === '1'}
                        onClick={() => toggleTab('1')}
                        icon={<MdDescription />}
                    >
                        {t('Items.ViewItem.tabs.description')}
                    </TabButton>
                    <TabButton
                        active={activeTab === '2'}
                        onClick={() => toggleTab('2')}
                        icon={<MdHistory />}
                    >
                        {t('Items.ViewItem.tabs.transactions')}
                    </TabButton>
                    <TabButton
                        active={activeTab === '3'}
                        onClick={() => toggleTab('3')}
                        icon={<MdInfo />}
                    >
                        {t('Items.ViewItem.tabs.details')}
                    </TabButton>
                    <TabButton
                        active={activeTab === '4'}
                        onClick={() => toggleTab('4')}
                        icon={<MdSecurity />}
                    >
                        {t('Items.ViewItem.tabs.warranty')}
                    </TabButton>
                </TabBar>
                <TabContent activeTab={activeTab}>
                    <TabPane tabId="1">
                        {loading ? (
                            <StyledCard>
                                <SpinnerWrapper>
                                    <Spinner />
                                </SpinnerWrapper>
                            </StyledCard>
                        ) : (
                            <OverviewGrid>
                                <StyledCard>
                                    <SummaryHeader>
                                        <div>
                                            <SummaryTitle>{itemDetails?.serial_number || '—'}</SummaryTitle>
                                            <SummarySubtitle>
                                                {itemDetails?.manufacturer_item?.product_name || '—'}
                                            </SummarySubtitle>
                                        </div>
                                        <StatusPill $tone={isInStock === null ? 'neutral' : isInStock ? 'success' : 'warning'}>
                                            {isInStock === null
                                                ? '—'
                                                : isInStock
                                                    ? t('Items.Overview.status_in_stock')
                                                    : t('Items.Overview.status_sold')}
                                        </StatusPill>
                                    </SummaryHeader>
                                    <MetaGrid>
                                        <MetaCard>
                                            <MetaTitle>{t('Items.ViewItem.details.serial_number')}</MetaTitle>
                                            <MetaValue>{itemDetails?.serial_number || '—'}</MetaValue>
                                        </MetaCard>
                                        <MetaCard>
                                            <MetaTitle>{t('Items.ViewItem.details.organization_sku')}</MetaTitle>
                                            <MetaValue>{itemDetails?.organization_sku?.organization_sku || '—'}</MetaValue>
                                        </MetaCard>
                                        <MetaCard>
                                            <MetaTitle>{t('Items.ViewItem.details.variant_sku')}</MetaTitle>
                                            <MetaValue>{itemDetails?.variant?.sku || '—'}</MetaValue>
                                        </MetaCard>
                                        <MetaCard>
                                            <MetaTitle>{t('Items.ViewItem.details.current_price')}</MetaTitle>
                                            <MetaValue>
                                                {formatResolvedAmount({
                                                    eurAmount: itemDetails?.current_price,
                                                    localAmount: itemDetails?.current_price_local ?? itemDetails?.price_seller_local,
                                                    localCurrencyId: itemDetails?.seller_currency_id ?? itemDetails?.currency_id,
                                                })}
                                            </MetaValue>
                                        </MetaCard>
                                        <MetaCard>
                                            <MetaTitle>{t('Items.ViewItem.details.last_sold_price')}</MetaTitle>
                                            <MetaValue>
                                                {formatResolvedAmount({
                                                    eurAmount: itemDetails?.last_sold_price,
                                                    localAmount: itemDetails?.last_sold_price_local,
                                                    localCurrencyId: itemDetails?.seller_currency_id ?? itemDetails?.currency_id,
                                                })}
                                            </MetaValue>
                                        </MetaCard>
                                        <MetaCard>
                                            <MetaTitle>{t('Items.ViewItem.details.start_date')}</MetaTitle>
                                            <MetaValue>
                                                {itemDetails?.start_date
                                                    ? new Date(itemDetails.start_date).toLocaleDateString()
                                                    : '—'}
                                            </MetaValue>
                                        </MetaCard>
                                    </MetaGrid>
                                    <MetaRows>
                                        <MetaRow>
                                            <MetaLabel>{t('Items.ViewItem.manufacturer')}</MetaLabel>
                                            <MetaText>{itemDetails?.manufacturer_item?.manufacturer?.name || '—'}</MetaText>
                                        </MetaRow>
                                        <MetaRow>
                                            <MetaLabel>{t('Items.ViewItem.category')}</MetaLabel>
                                            <MetaText>{itemDetails?.manufacturer_item?.category_core?.path || itemDetails?.manufacturer_item?.category_core?.name || '—'}</MetaText>
                                        </MetaRow>
                                        <MetaRow>
                                            <MetaLabel>{t('Items.ViewItem.details.discount')}</MetaLabel>
                                            <MetaText>{itemDetails?.discount ?? '—'}</MetaText>
                                        </MetaRow>
                                    </MetaRows>
                                </StyledCard>
                                <StyledCard>
                                    <ProductSummary>
                                        <ImageContainer>
                                            {itemDetails?.manufacturer_item?.image_url ? (
                                                <ProductImage
                                                    src={itemDetails.manufacturer_item.image_url}
                                                    alt={itemDetails.manufacturer_item.product_name || ''}
                                                />
                                            ) : (
                                                <ImageFallback>No image</ImageFallback>
                                            )}
                                            {itemDetails?.manufacturer_item?.manufacturer?.logo && (
                                                <ManufacturerLogo
                                                    src={itemDetails.manufacturer_item.manufacturer.logo}
                                                    alt={itemDetails.manufacturer_item.manufacturer.name || ''}
                                                />
                                            )}
                                        </ImageContainer>
                                        <DescriptionContainer>
                                            <h3>{itemDetails?.manufacturer_item?.product_name || '—'}</h3>
                                            {itemDetails?.manufacturer_item?.description ? (
                                                <Description
                                                    dangerouslySetInnerHTML={{
                                                        __html: itemDetails.manufacturer_item.description
                                                    }}
                                                />
                                            ) : (
                                                <DescriptionMuted>—</DescriptionMuted>
                                            )}
                                        </DescriptionContainer>
                                    </ProductSummary>
                                </StyledCard>
                            </OverviewGrid>
                        )}
                    </TabPane>
                    <TabPane tabId="2">
                        <StyledCard>
                            <ItemTransactionsTable itemId={itemId} />
                        </StyledCard>
                    </TabPane>
                    <TabPane tabId="3">
                        {loading ? (
                            <StyledCard>
                                <SpinnerWrapper>
                                    <Spinner />
                                </SpinnerWrapper>
                            </StyledCard>
                        ) : (
                            <DetailsGrid>
                                <DetailCard>
                                    <DetailCardTitle>{t('Items.ViewItem.tabs.details')}</DetailCardTitle>
                                    <DetailRow>
                                        <DetailLabel>{t('Items.ViewItem.details.model_number')}</DetailLabel>
                                        <DetailValue>{itemDetails?.manufacturer_item?.model_number || '—'}</DetailValue>
                                    </DetailRow>
                                    <DetailRow>
                                        <DetailLabel>{t('Items.ViewItem.details.release_year')}</DetailLabel>
                                        <DetailValue>{itemDetails?.manufacturer_item?.release_year || 'N/A'}</DetailValue>
                                    </DetailRow>
                                    <DetailRow>
                                        <DetailLabel>{t('Items.ViewItem.details.source_id')}</DetailLabel>
                                        <DetailValue>{itemDetails?.source_id || '—'}</DetailValue>
                                    </DetailRow>
                                    <DetailRow>
                                        <DetailLabel>{t('Items.ViewItem.details.icecat_id')}</DetailLabel>
                                        <DetailValue>{itemDetails?.manufacturer_item?.icecat_id || '—'}</DetailValue>
                                    </DetailRow>
                                    <DetailRow>
                                        <DetailLabel>{t('Items.ViewItem.details.category')}</DetailLabel>
                                        <DetailValue>{itemDetails?.manufacturer_item?.category_core?.path || itemDetails?.manufacturer_item?.category_core?.name || '—'}</DetailValue>
                                    </DetailRow>
                                </DetailCard>
                                <DetailCard>
                                    <DetailCardTitle>{t('Items.ViewItem.details.technical_specifications')}</DetailCardTitle>
                                    <SpecList>
                                        {itemDetails?.manufacturer_item?.technical_specifications
                                            ? Object.entries(
                                                typeof itemDetails.manufacturer_item.technical_specifications === 'string'
                                                    ? JSON.parse(itemDetails.manufacturer_item.technical_specifications)
                                                    : itemDetails.manufacturer_item.technical_specifications
                                            ).map(([key, val]) => (
                                                <SpecItem key={key}>
                                                    <SpecKey>{key}:</SpecKey>
                                                    <SpecVal>{val}</SpecVal>
                                                </SpecItem>
                                            ))
                                            : '—'}
                                    </SpecList>
                                </DetailCard>
                                <DetailCard>
                                    <DetailCardTitle>{t('Items.ViewItem.details.initial_price')}</DetailCardTitle>
                                    <DetailRow>
                                        <DetailLabel>{t('Items.ViewItem.details.initial_price')}</DetailLabel>
                                        <DetailValue>{formatResolvedAmount({
                                            eurAmount: itemDetails?.manufacturer_item?.initial_price,
                                            localAmount: itemDetails?.manufacturer_item?.initial_price_local,
                                            localCurrencyId: itemDetails?.currency_id ?? itemDetails?.seller_currency_id,
                                        })}</DetailValue>
                                    </DetailRow>
                                    <DetailRow>
                                        <DetailLabel>{t('Items.ViewItem.details.current_price')}</DetailLabel>
                                        <DetailValue>{formatResolvedAmount({
                                            eurAmount: itemDetails?.current_price,
                                            localAmount: itemDetails?.current_price_local ?? itemDetails?.price_seller_local,
                                            localCurrencyId: itemDetails?.seller_currency_id ?? itemDetails?.currency_id,
                                        })}</DetailValue>
                                    </DetailRow>
                                    <DetailRow>
                                        <DetailLabel>{t('Items.ViewItem.details.last_sold_price')}</DetailLabel>
                                        <DetailValue>{formatResolvedAmount({
                                            eurAmount: itemDetails?.last_sold_price,
                                            localAmount: itemDetails?.last_sold_price_local,
                                            localCurrencyId: itemDetails?.seller_currency_id ?? itemDetails?.currency_id,
                                        })}</DetailValue>
                                    </DetailRow>
                                    <DetailRow>
                                        <DetailLabel>{t('Items.ViewItem.details.discount')}</DetailLabel>
                                        <DetailValue>{itemDetails?.discount ?? '—'}</DetailValue>
                                    </DetailRow>
                                </DetailCard>
                                <DetailCard>
                                    <DetailCardTitle>{t('Items.ViewItem.details.material_composition')}</DetailCardTitle>
                                    <DetailRow>
                                        <DetailLabel>{t('Items.ViewItem.details.material_composition')}</DetailLabel>
                                        <DetailValue>{itemDetails?.manufacturer_item?.material_composition || 'N/A'}</DetailValue>
                                    </DetailRow>
                                    <DetailRow>
                                        <DetailLabel>{t('Items.ViewItem.details.carbon_footprint')}</DetailLabel>
                                        <DetailValue>{itemDetails?.manufacturer_item?.carbon_footprint || 'N/A'}</DetailValue>
                                    </DetailRow>
                                    <DetailRow>
                                        <DetailLabel>{t('Items.ViewItem.details.recycling_instructions')}</DetailLabel>
                                        <DetailValue>{itemDetails?.manufacturer_item?.recycling_instructions || 'N/A'}</DetailValue>
                                    </DetailRow>
                                    <DetailRow>
                                        <DetailLabel>{t('Items.ViewItem.details.certifications')}</DetailLabel>
                                        <DetailValue>
                                            {itemDetails?.manufacturer_item?.certifications
                                                ? JSON.stringify(itemDetails.manufacturer_item.certifications)
                                                : 'N/A'}
                                        </DetailValue>
                                    </DetailRow>
                                    <DetailRow>
                                        <DetailLabel>{t('Items.ViewItem.details.documentation')}</DetailLabel>
                                        <DetailValue>
                                            {itemDetails?.manufacturer_item?.documentation_url ? (
                                                <a
                                                    href={itemDetails.manufacturer_item.documentation_url}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    {t('Items.ViewItem.details.view_documentation')}
                                                </a>
                                            ) : (
                                                '—'
                                            )}
                                        </DetailValue>
                                    </DetailRow>
                                </DetailCard>
                            </DetailsGrid>
                        )}
                    </TabPane>
                    <TabPane tabId="4">
                        {loading ? (
                            <StyledCard>
                                <SpinnerWrapper>
                                    <Spinner />
                                </SpinnerWrapper>
                            </StyledCard>
                        ) : (
                            <WarrantyGrid>
                                <StyledCard>
                                    <WarrantyHeader>
                                        <h4>{t('Items.ViewItem.warranty.title')}</h4>
                                        <WarrantyStatus>
                                            {itemDetails?.manufacturer_item?.warranty_template?.is_active ? 'Active' : 'Inactive'}
                                        </WarrantyStatus>
                                    </WarrantyHeader>
                                    <WarrantyRow>
                                        <WarrantyLabel>{t('Items.ViewItem.warranty.name')}</WarrantyLabel>
                                        <WarrantyValue>{itemDetails?.manufacturer_item?.warranty_template?.name || '—'}</WarrantyValue>
                                    </WarrantyRow>
                                    <WarrantyRow>
                                        <WarrantyLabel>{t('Items.ViewItem.warranty.duration')}</WarrantyLabel>
                                        <WarrantyValue>
                                            {itemDetails?.manufacturer_item?.warranty_template?.duration_months || '—'} months
                                        </WarrantyValue>
                                    </WarrantyRow>
                                    <WarrantyRow>
                                        <WarrantyLabel>{t('Items.ViewItem.warranty.coverage')}</WarrantyLabel>
                                        <WarrantyValue>
                                            {itemDetails?.manufacturer_item?.warranty_template?.terms?.coverage
                                                ? JSON.stringify(itemDetails.manufacturer_item.warranty_template.terms.coverage)
                                                : '—'}
                                        </WarrantyValue>
                                    </WarrantyRow>
                                    <WarrantyRow>
                                        <WarrantyLabel>{t('Items.ViewItem.warranty.exclusions')}</WarrantyLabel>
                                        <WarrantyValue>
                                            {itemDetails?.manufacturer_item?.warranty_template?.terms?.exclusions?.join(', ') || '—'}
                                        </WarrantyValue>
                                    </WarrantyRow>
                                </StyledCard>
                                <StyledCard>
                                    <WarrantyHeader>
                                        <h4>{t('Items.ViewItem.warranty.claim_process')}</h4>
                                    </WarrantyHeader>
                                    <WarrantyRow>
                                        <WarrantyLabel>{t('Items.ViewItem.warranty.claim_process')}</WarrantyLabel>
                                        <WarrantyValue>
                                            {itemDetails?.manufacturer_item?.warranty_template?.claim_process?.process || '—'}
                                        </WarrantyValue>
                                    </WarrantyRow>
                                    <WarrantyRow>
                                        <WarrantyLabel>{t('Items.ViewItem.warranty.contact')}</WarrantyLabel>
                                        <WarrantyValue>
                                            {itemDetails?.manufacturer_item?.warranty_template?.claim_process?.contact || '—'}
                                        </WarrantyValue>
                                    </WarrantyRow>
                                    <WarrantyRow>
                                        <WarrantyLabel>{t('Items.ViewItem.warranty.applicable_to')}</WarrantyLabel>
                                        <WarrantyValue>
                                            {itemDetails?.manufacturer_item?.warranty_template?.applicable_to?.regions?.join(', ') || '—'}
                                        </WarrantyValue>
                                    </WarrantyRow>
                                </StyledCard>
                            </WarrantyGrid>
                        )}
                    </TabPane>
                </TabContent>
            </PageContent>
        </PageContainer>
    );
};

export default ViewItem;

// Styled Components
const OverviewGrid = styled.div`
    display: grid;
    gap: ${({ theme }) => theme?.spacing?.medium || '1.25rem'};

    @media (min-width: ${({ theme }) => theme?.breakpoints?.mobile || '768px'}) {
        grid-template-columns: 1.1fr 1fr;
    }
`;

const SummaryHeader = styled.div`
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    gap: 0.75rem;
    margin-bottom: 1.25rem;
`;

const SummaryTitle = styled.h3`
    margin: 0;
    font-size: ${({ theme }) => theme?.fontSizes?.large || '1.5rem'};
`;

const SummarySubtitle = styled.div`
    margin-top: 0.375rem;
    color: ${({ theme }) => theme?.colors?.textSecondary || '#5a5a5a'};
    font-size: ${({ theme }) => theme?.fontSizes?.small || '0.95rem'};
`;

const StatusPill = styled.span`
    padding: 0.375rem 0.75rem;
    border-radius: 999px;
    font-weight: 600;
    font-size: 0.875rem;
    background: ${({ $tone }) => {
        switch ($tone) {
            case 'success':
                return 'rgba(34, 197, 94, 0.15)';
            case 'warning':
                return 'rgba(245, 158, 11, 0.18)';
            default:
                return 'rgba(148, 163, 184, 0.2)';
        }
    }};
    color: ${({ $tone }) => {
        switch ($tone) {
            case 'success':
                return '#15803d';
            case 'warning':
                return '#b45309';
            default:
                return '#475569';
        }
    }};
`;

const MetaGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
    gap: 0.75rem;
    margin-bottom: 1.25rem;
`;

const MetaCard = styled.div`
    background: rgba(130, 22, 146, 0.06);
    border-radius: 12px;
    padding: 0.75rem;
`;

const MetaTitle = styled.div`
    font-size: 0.8125rem;
    color: ${({ theme }) => theme?.colors?.textSecondary || '#6b7280'};
    margin-bottom: 0.375rem;
    text-transform: uppercase;
    letter-spacing: 0.04em;
`;

const MetaValue = styled.div`
    font-weight: 600;
    font-size: 0.95rem;
`;

const MetaRows = styled.div`
    display: grid;
    gap: 0.625rem;
`;

const MetaRow = styled.div`
    display: flex;
    justify-content: space-between;
    gap: 0.75rem;
`;

const MetaLabel = styled.span`
    color: ${({ theme }) => theme?.colors?.textSecondary || '#6b7280'};
    font-weight: 600;
`;

const MetaText = styled.span`
    text-align: right;
`;

const ProductSummary = styled.div`
    display: grid;
    gap: 1rem;
    align-items: start;

    @media (min-width: ${({ theme }) => theme?.breakpoints?.mobile || '768px'}) {
        grid-template-columns: 220px 1fr;
    }
`;

const ImageContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: ${({ theme }) => theme?.spacing?.small || '0.625rem'};
    background: rgba(130, 22, 146, 0.04);
    padding: 1rem;
    border-radius: 12px;
`;

const ProductImage = styled.img`
    width: 100%;
    max-width: 300px;
    height: auto;
    border-radius: 8px;
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
`;

const ImageFallback = styled.div`
    width: 100%;
    max-width: 300px;
    height: 200px;
    border-radius: 8px;
    background: rgba(148, 163, 184, 0.2);
    color: ${({ theme }) => theme?.colors?.textSecondary || '#6b7280'};
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 600;
`;

const ManufacturerLogo = styled.img`
    width: 100px;
    height: auto;
    border-radius: 8px;
`;

const DescriptionContainer = styled.div`
  flex: 1;
  text-align: left;

  h3 {
    font-size: ${({ theme }) => theme?.fontSizes?.large || '1.5rem'};
    color: ${({ theme }) => theme?.colors?.brand || 'rgba(130, 22, 146, 1)'};
    margin-bottom: ${({ theme }) => theme?.spacing?.medium || '1.25rem'};
  }

  p {
    font-size: ${({ theme }) => theme?.fontSizes?.medium || '1.1rem'};
    margin-bottom: ${({ theme }) => theme?.spacing?.small || '0.625rem'};
  }
`;

const Description = styled.div`
    font-size: ${({ theme }) => theme?.fontSizes?.medium || '1.1rem'};
    line-height: 1.6;
    margin-bottom: ${({ theme }) => theme?.spacing?.medium || '1.25rem'};
`;

const DescriptionMuted = styled.div`
    color: ${({ theme }) => theme?.colors?.textSecondary || '#6b7280'};
    font-size: ${({ theme }) => theme?.fontSizes?.medium || '1.1rem'};
`;

const DetailsGrid = styled.div`
    display: grid;
    gap: ${({ theme }) => theme?.spacing?.medium || '1.25rem'};

    @media (min-width: ${({ theme }) => theme?.breakpoints?.mobile || '768px'}) {
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }
`;

const DetailCard = styled(StyledCard)`
    height: 100%;
`;

const DetailCardTitle = styled.h4`
    margin: 0 0 1rem;
    font-size: ${({ theme }) => theme?.fontSizes?.medium || '1.1rem'};
    color: ${({ theme }) => theme?.colors?.brand || 'rgba(130, 22, 146, 1)'};
`;

const DetailRow = styled.div`
    display: flex;
    justify-content: space-between;
    padding: ${({ theme }) => theme?.spacing?.tiny || '0.3125rem'} 0;
    border-bottom: 1px solid #e0e0e0;
`;

const DetailLabel = styled.strong`
    flex: 1;
    font-size: ${({ theme }) => theme?.fontSizes?.medium || '1.1rem'};
`;

const DetailValue = styled.span`
    flex: 2;
    text-align: right;
    font-size: ${({ theme }) => theme?.fontSizes?.medium || '1.1rem'};
`;

const SpecItem = styled.div`
    display: flex;
    justify-content: space-between;
    margin-bottom: ${({ theme }) => theme?.spacing?.tiny || '0.3125rem'};
`;

const SpecList = styled.div`
    display: grid;
    gap: 0.5rem;
`;

const SpecKey = styled.strong`
    flex: 1;
    font-size: ${({ theme }) => theme?.fontSizes?.small || '0.9rem'};
`;

const SpecVal = styled.span`
    flex: 2;
    text-align: right;
    font-size: ${({ theme }) => theme?.fontSizes?.small || '0.9rem'};
`;

const WarrantyGrid = styled.div`
    display: grid;
    gap: ${({ theme }) => theme?.spacing?.medium || '1.25rem'};

    @media (min-width: ${({ theme }) => theme?.breakpoints?.mobile || '768px'}) {
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }
`;

const WarrantyHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.75rem;
    margin-bottom: 1rem;

    h4 {
        margin: 0;
        font-size: ${({ theme }) => theme?.fontSizes?.large || '1.4rem'};
        color: ${({ theme }) => theme?.colors?.brand || 'rgba(130, 22, 146, 1)'};
    }
`;

const WarrantyStatus = styled.span`
    padding: 0.375rem 0.75rem;
    border-radius: 999px;
    background: rgba(130, 22, 146, 0.12);
    color: ${({ theme }) => theme?.colors?.brand || 'rgba(130, 22, 146, 1)'};
    font-weight: 600;
    font-size: 0.875rem;
`;

const WarrantyRow = styled.div`
    display: grid;
    gap: 0.375rem;
    padding: 0.75rem 0;
    border-bottom: 1px solid rgba(148, 163, 184, 0.2);
`;

const WarrantyLabel = styled.span`
    font-size: 0.875rem;
    font-weight: 600;
    color: ${({ theme }) => theme?.colors?.textSecondary || '#6b7280'};
    text-transform: uppercase;
    letter-spacing: 0.04em;
`;

const WarrantyValue = styled.span`
    font-size: ${({ theme }) => theme?.fontSizes?.medium || '1.05rem'};
`;

const SpinnerWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 200px;
`;
